/**
 * HVAC Component Definitions
 *
 * Pre-built definitions for HVAC equipment components used in the visual editor.
 * Each definition includes ports, IDF mapping, and default values.
 */

import type { HVACComponentDefinition, ComponentCategory } from './types';

/**
 * All HVAC component definitions
 */
export const HVAC_COMPONENTS: HVACComponentDefinition[] = [
  // ============================================
  // FANS
  // ============================================
  {
    type: 'fan-variable-volume',
    displayName: 'Variable Volume Fan',
    category: 'fans',
    icon: 'fan',
    idfClass: 'Fan:VariableVolume',
    description: 'Variable air volume fan with speed control',
    color: '#4CAF50',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Fan Total Efficiency': '0.7',
      'Pressure Rise': '1000',
      'Motor Efficiency': '0.9',
      'Motor In Airstream Fraction': '1.0',
    },
  },
  {
    type: 'fan-constant-volume',
    displayName: 'Constant Volume Fan',
    category: 'fans',
    icon: 'fan',
    idfClass: 'Fan:ConstantVolume',
    description: 'Constant air volume fan',
    color: '#4CAF50',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Fan Total Efficiency': '0.7',
      'Pressure Rise': '600',
      'Motor Efficiency': '0.9',
      'Motor In Airstream Fraction': '1.0',
    },
  },
  {
    type: 'fan-on-off',
    displayName: 'On/Off Fan',
    category: 'fans',
    icon: 'fan',
    idfClass: 'Fan:OnOff',
    description: 'Simple on/off cycling fan',
    color: '#4CAF50',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Fan Total Efficiency': '0.7',
      'Pressure Rise': '300',
      'Motor Efficiency': '0.9',
      'Motor In Airstream Fraction': '1.0',
    },
  },

  // ============================================
  // COOLING COILS
  // ============================================
  {
    type: 'coil-cooling-dx-single-speed',
    displayName: 'DX Cooling Coil (Single Speed)',
    category: 'cooling-coils',
    icon: 'snowflake',
    idfClass: 'Coil:Cooling:DX:SingleSpeed',
    description: 'Direct expansion single-speed cooling coil',
    color: '#2196F3',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Gross Rated Total Cooling Capacity': 'autosize',
      'Gross Rated Sensible Heat Ratio': 'autosize',
      'Gross Rated Cooling COP': '3.0',
      'Rated Air Flow Rate': 'autosize',
    },
  },
  {
    type: 'coil-cooling-dx-two-speed',
    displayName: 'DX Cooling Coil (Two Speed)',
    category: 'cooling-coils',
    icon: 'snowflake',
    idfClass: 'Coil:Cooling:DX:TwoSpeed',
    description: 'Direct expansion two-speed cooling coil',
    color: '#2196F3',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'High Speed Gross Rated Total Cooling Capacity': 'autosize',
      'High Speed Gross Rated Sensible Heat Ratio': 'autosize',
      'High Speed Gross Rated Cooling COP': '3.0',
      'High Speed Rated Air Flow Rate': 'autosize',
    },
  },
  {
    type: 'coil-cooling-water',
    displayName: 'Chilled Water Coil',
    category: 'cooling-coils',
    icon: 'snowflake',
    idfClass: 'Coil:Cooling:Water',
    description: 'Chilled water cooling coil',
    color: '#2196F3',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.25,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.25,
        required: true,
      },
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.75,
        required: true,
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.75,
        required: true,
      },
    ],
    defaults: {
      'Design Water Flow Rate': 'autosize',
      'Design Air Flow Rate': 'autosize',
      'Design Inlet Water Temperature': '7',
      'Design Inlet Air Temperature': '24',
    },
  },

  // ============================================
  // HEATING COILS
  // ============================================
  {
    type: 'coil-heating-water',
    displayName: 'Hot Water Coil',
    category: 'heating-coils',
    icon: 'flame',
    idfClass: 'Coil:Heating:Water',
    description: 'Hot water heating coil',
    color: '#FF5722',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.25,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.25,
        required: true,
      },
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.75,
        required: true,
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.75,
        required: true,
      },
    ],
    defaults: {
      'U-Factor Times Area Value': 'autosize',
      'Maximum Water Flow Rate': 'autosize',
      'Rated Inlet Water Temperature': '82.2',
      'Rated Inlet Air Temperature': '16.6',
    },
  },
  {
    type: 'coil-heating-electric',
    displayName: 'Electric Heating Coil',
    category: 'heating-coils',
    icon: 'flame',
    idfClass: 'Coil:Heating:Electric',
    description: 'Electric resistance heating coil',
    color: '#FF5722',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      Efficiency: '1.0',
      'Nominal Capacity': 'autosize',
    },
  },
  {
    type: 'coil-heating-gas',
    displayName: 'Gas Heating Coil',
    category: 'heating-coils',
    icon: 'flame',
    idfClass: 'Coil:Heating:Fuel',
    description: 'Gas-fired heating coil',
    color: '#FF5722',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Fuel Type': 'NaturalGas',
      'Burner Efficiency': '0.8',
      'Nominal Capacity': 'autosize',
    },
  },
  {
    type: 'coil-heating-dx-single-speed',
    displayName: 'DX Heating Coil',
    category: 'heating-coils',
    icon: 'flame',
    idfClass: 'Coil:Heating:DX:SingleSpeed',
    description: 'Direct expansion heat pump heating coil',
    color: '#FF5722',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Gross Rated Heating Capacity': 'autosize',
      'Gross Rated Heating COP': '2.75',
      'Rated Air Flow Rate': 'autosize',
    },
  },

  // ============================================
  // AIR HANDLING
  // ============================================
  {
    type: 'heat-exchanger-air-to-air',
    displayName: 'Air-to-Air Heat Exchanger',
    category: 'air-handling',
    icon: 'arrows-exchange',
    idfClass: 'HeatExchanger:AirToAir:SensibleAndLatent',
    description: 'Sensible and latent heat recovery',
    color: '#9C27B0',
    ports: [
      {
        id: 'supply-air-inlet',
        name: 'Supply Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.25,
        required: true,
      },
      {
        id: 'supply-air-outlet',
        name: 'Supply Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.25,
        required: true,
      },
      {
        id: 'exhaust-air-inlet',
        name: 'Exhaust Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.75,
        required: true,
      },
      {
        id: 'exhaust-air-outlet',
        name: 'Exhaust Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.75,
        required: true,
      },
    ],
    defaults: {
      'Nominal Supply Air Flow Rate': 'autosize',
      'Sensible Effectiveness at 100% Heating Air Flow': '0.76',
      'Latent Effectiveness at 100% Heating Air Flow': '0.68',
      'Sensible Effectiveness at 100% Cooling Air Flow': '0.76',
      'Latent Effectiveness at 100% Cooling Air Flow': '0.68',
    },
  },
  {
    type: 'humidifier-steam-electric',
    displayName: 'Electric Steam Humidifier',
    category: 'air-handling',
    icon: 'droplet',
    idfClass: 'Humidifier:Steam:Electric',
    description: 'Electric steam humidifier',
    color: '#9C27B0',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Rated Capacity': 'autosize',
      'Rated Power': 'autosize',
    },
  },
  {
    type: 'outdoor-air-system',
    displayName: 'Outdoor Air System',
    category: 'air-handling',
    icon: 'wind',
    idfClass: 'AirLoopHVAC:OutdoorAirSystem',
    description: 'Outdoor air intake and exhaust system',
    color: '#9C27B0',
    ports: [
      {
        id: 'return-air-inlet',
        name: 'Return Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.25,
        required: true,
      },
      {
        id: 'mixed-air-outlet',
        name: 'Mixed Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'relief-air-outlet',
        name: 'Relief Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.75,
        required: false,
      },
    ],
    defaults: {
      'Controller Type': 'Controller:OutdoorAir',
    },
  },
  {
    type: 'unitary-heat-pump',
    displayName: 'Unitary Heat Pump',
    category: 'air-handling',
    icon: 'box',
    idfClass: 'AirLoopHVAC:UnitaryHeatPump:AirToAir',
    description: 'Packaged air-to-air heat pump system',
    color: '#FF9800',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Cooling Supply Air Flow Rate': 'autosize',
      'Heating Supply Air Flow Rate': 'autosize',
    },
  },
  {
    type: 'unitary-system',
    displayName: 'Unitary System',
    category: 'air-handling',
    icon: 'box',
    idfClass: 'AirLoopHVAC:UnitarySystem',
    description: 'Packaged unitary HVAC system',
    color: '#FF9800',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Control Type': 'Load',
    },
  },

  // ============================================
  // ZONE EQUIPMENT
  // ============================================
  {
    type: 'vav-box-reheat',
    displayName: 'VAV Box with Reheat',
    category: 'zone-equipment',
    icon: 'box',
    idfClass: 'AirTerminal:SingleDuct:VAV:Reheat',
    description: 'Variable air volume terminal with reheat coil',
    color: '#607D8B',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'hw-inlet',
        name: 'HW Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.75,
        required: false,
      },
      {
        id: 'hw-outlet',
        name: 'HW Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.75,
        required: false,
      },
    ],
    defaults: {
      'Maximum Air Flow Rate': 'autosize',
      'Zone Minimum Air Flow Input Method': 'Constant',
      'Constant Minimum Air Flow Fraction': '0.3',
      'Damper Heating Action': 'Normal',
    },
  },
  {
    type: 'vav-box-no-reheat',
    displayName: 'VAV Box (No Reheat)',
    category: 'zone-equipment',
    icon: 'box',
    idfClass: 'AirTerminal:SingleDuct:VAV:NoReheat',
    description: 'Variable air volume terminal without reheat',
    color: '#607D8B',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Maximum Air Flow Rate': 'autosize',
      'Zone Minimum Air Flow Input Method': 'Constant',
      'Constant Minimum Air Flow Fraction': '0.3',
    },
  },
  {
    type: 'cav-box',
    displayName: 'CAV Box',
    category: 'zone-equipment',
    icon: 'box',
    idfClass: 'AirTerminal:SingleDuct:ConstantVolume:NoReheat',
    description: 'Constant air volume terminal',
    color: '#607D8B',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Maximum Air Flow Rate': 'autosize',
    },
  },
  {
    type: 'diffuser',
    displayName: 'Air Diffuser',
    category: 'zone-equipment',
    icon: 'grid',
    idfClass: 'AirTerminal:SingleDuct:Uncontrolled',
    description: 'Uncontrolled air diffuser',
    color: '#607D8B',
    ports: [
      {
        id: 'air-inlet',
        name: 'Air Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'air-outlet',
        name: 'Air Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Maximum Air Flow Rate': 'autosize',
    },
  },

  // ============================================
  // STRUCTURAL
  // ============================================
  {
    type: 'supply-splitter',
    displayName: 'Supply Splitter',
    category: 'structural',
    icon: 'split',
    idfClass: 'AirLoopHVAC:ZoneSplitter',
    description: 'Splits supply air to multiple zones',
    color: '#795548',
    ports: [
      {
        id: 'air-inlet',
        name: 'Inlet',
        direction: 'inlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
      {
        id: 'outlet-1',
        name: 'Outlet 1',
        direction: 'outlet',
        medium: 'air',
        position: 0.2,
        required: true,
      },
      {
        id: 'outlet-2',
        name: 'Outlet 2',
        direction: 'outlet',
        medium: 'air',
        position: 0.4,
        required: false,
      },
      {
        id: 'outlet-3',
        name: 'Outlet 3',
        direction: 'outlet',
        medium: 'air',
        position: 0.6,
        required: false,
      },
      {
        id: 'outlet-4',
        name: 'Outlet 4',
        direction: 'outlet',
        medium: 'air',
        position: 0.8,
        required: false,
      },
    ],
    defaults: {},
  },
  {
    type: 'return-mixer',
    displayName: 'Return Mixer',
    category: 'structural',
    icon: 'merge',
    idfClass: 'AirLoopHVAC:ZoneMixer',
    description: 'Mixes return air from multiple zones',
    color: '#795548',
    ports: [
      {
        id: 'inlet-1',
        name: 'Inlet 1',
        direction: 'inlet',
        medium: 'air',
        position: 0.2,
        required: true,
      },
      {
        id: 'inlet-2',
        name: 'Inlet 2',
        direction: 'inlet',
        medium: 'air',
        position: 0.4,
        required: false,
      },
      {
        id: 'inlet-3',
        name: 'Inlet 3',
        direction: 'inlet',
        medium: 'air',
        position: 0.6,
        required: false,
      },
      {
        id: 'inlet-4',
        name: 'Inlet 4',
        direction: 'inlet',
        medium: 'air',
        position: 0.8,
        required: false,
      },
      {
        id: 'air-outlet',
        name: 'Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {},
  },
  {
    type: 'return-plenum',
    displayName: 'Return Plenum',
    category: 'structural',
    icon: 'square',
    idfClass: 'AirLoopHVAC:ReturnPlenum',
    description: 'Return air plenum for collecting zone return air',
    color: '#795548',
    ports: [
      {
        id: 'inlet-1',
        name: 'Zone Inlet 1',
        direction: 'inlet',
        medium: 'air',
        position: 0.2,
        required: true,
      },
      {
        id: 'inlet-2',
        name: 'Zone Inlet 2',
        direction: 'inlet',
        medium: 'air',
        position: 0.4,
        required: false,
      },
      {
        id: 'inlet-3',
        name: 'Zone Inlet 3',
        direction: 'inlet',
        medium: 'air',
        position: 0.6,
        required: false,
      },
      {
        id: 'inlet-4',
        name: 'Zone Inlet 4',
        direction: 'inlet',
        medium: 'air',
        position: 0.8,
        required: false,
      },
      {
        id: 'air-outlet',
        name: 'Return Outlet',
        direction: 'outlet',
        medium: 'air',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {},
  },

  // ============================================
  // PLANT EQUIPMENT
  // ============================================
  {
    type: 'chiller-electric',
    displayName: 'Electric Chiller',
    category: 'plant',
    icon: 'snowflake',
    idfClass: 'Chiller:Electric:EIR',
    description: 'Electric chiller with EIR model',
    color: '#00BCD4',
    ports: [
      {
        id: 'chw-inlet',
        name: 'CHW Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.25,
        required: true,
      },
      {
        id: 'chw-outlet',
        name: 'CHW Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.25,
        required: true,
      },
      {
        id: 'cw-inlet',
        name: 'CW Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.75,
        required: true,
      },
      {
        id: 'cw-outlet',
        name: 'CW Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.75,
        required: true,
      },
    ],
    defaults: {
      'Reference Capacity': 'autosize',
      'Reference COP': '5.5',
      'Reference Leaving Chilled Water Temperature': '6.67',
      'Reference Entering Condenser Fluid Temperature': '29.4',
    },
  },
  {
    type: 'boiler-hot-water',
    displayName: 'Hot Water Boiler',
    category: 'plant',
    icon: 'flame',
    idfClass: 'Boiler:HotWater',
    description: 'Gas-fired hot water boiler',
    color: '#FF9800',
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Fuel Type': 'NaturalGas',
      'Nominal Capacity': 'autosize',
      'Nominal Thermal Efficiency': '0.8',
      'Design Water Flow Rate': 'autosize',
    },
  },
  {
    type: 'cooling-tower',
    displayName: 'Cooling Tower',
    category: 'plant',
    icon: 'tower',
    idfClass: 'CoolingTower:SingleSpeed',
    description: 'Single-speed cooling tower',
    color: '#00BCD4',
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Design Water Flow Rate': 'autosize',
      'Design Air Flow Rate': 'autosize',
      'Design Fan Power': 'autosize',
    },
  },
  {
    type: 'pump-variable-speed',
    displayName: 'Variable Speed Pump',
    category: 'plant',
    icon: 'pump',
    idfClass: 'Pump:VariableSpeed',
    description: 'Variable speed water pump',
    color: '#3F51B5',
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Design Maximum Flow Rate': 'autosize',
      'Design Pump Head': '179352',
      'Design Power Consumption': 'autosize',
      'Motor Efficiency': '0.9',
    },
  },
  {
    type: 'pump-constant-speed',
    displayName: 'Constant Speed Pump',
    category: 'plant',
    icon: 'pump',
    idfClass: 'Pump:ConstantSpeed',
    description: 'Constant speed water pump',
    color: '#3F51B5',
    ports: [
      {
        id: 'water-inlet',
        name: 'Water Inlet',
        direction: 'inlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
      {
        id: 'water-outlet',
        name: 'Water Outlet',
        direction: 'outlet',
        medium: 'water',
        position: 0.5,
        required: true,
      },
    ],
    defaults: {
      'Design Flow Rate': 'autosize',
      'Design Pump Head': '179352',
      'Design Power Consumption': 'autosize',
      'Motor Efficiency': '0.9',
    },
  },
];

/**
 * Component definition lookup by type
 */
export const COMPONENT_BY_TYPE = new Map<string, HVACComponentDefinition>(
  HVAC_COMPONENTS.map((c) => [c.type, c])
);

/**
 * Get component definition by type
 */
export function getComponentDefinition(type: string): HVACComponentDefinition | undefined {
  return COMPONENT_BY_TYPE.get(type);
}

/**
 * Get components by category
 */
export function getComponentsByCategory(category: ComponentCategory): HVACComponentDefinition[] {
  return HVAC_COMPONENTS.filter((c) => c.category === category);
}

/**
 * Category display names
 */
export const CATEGORY_NAMES: Record<ComponentCategory, string> = {
  fans: 'Fans',
  'cooling-coils': 'Cooling Coils',
  'heating-coils': 'Heating Coils',
  'air-handling': 'Air Handling',
  'zone-equipment': 'Zone Equipment',
  structural: 'Structural',
  plant: 'Plant Equipment',
};

/**
 * All categories in display order
 */
export const CATEGORIES: ComponentCategory[] = [
  'fans',
  'cooling-coils',
  'heating-coils',
  'air-handling',
  'zone-equipment',
  'structural',
  'plant',
];
